import React, { useEffect, useState, useRef, useMemo } from "react";

import axios from "axios";
import ReactDOM from "react-dom";
import {
  Filter,
  Plus,
  Boxes,
  X,
  RefreshCw,
  CircleCheck,
  Loader2,
  Rocket,
  ChevronDown,
  ChevronRight,
  Search,
  Hash,
  CalendarDays,
  ToggleLeft,
  Link2,
  List,
  Type as TypeIcon,
} from "lucide-react";

/* ---------- type-aware operator catalogs ----------
 * Every value here must be one of the operators build_clause()/
 * parse_conditions_recursively() in backend/tables_and_fields/filter_parser.py
 * actually implement: is, !=, contains, startsWith, endsWith, >, <, >=, <=,
 * isnull, in, not_in. We only ever narrow which of those are *offered* per
 * field type — never invent new operator keywords the backend can't parse. */
const OPERATOR_LABELS = {
  is: "is",
  "!=": "is not",
  contains: "contains",
  startsWith: "starts with",
  endsWith: "ends with",
  ">": "greater than",
  "<": "less than",
  ">=": "at least",
  "<=": "at most",
  isnull: "is empty",
  in: "is one of",
  not_in: "is not one of",
};

const DATE_OPERATOR_LABELS = {
  is: "on",
  "!=": "not on",
  ">": "after",
  "<": "before",
  ">=": "on or after",
  "<=": "on or before",
  isnull: "is empty",
};

const OPERATORS_TEXT = ["is", "!=", "contains", "startsWith", "endsWith", "in", "not_in", "isnull"];
const OPERATORS_NUMBER = ["is", "!=", ">", "<", ">=", "<=", "in", "not_in", "isnull"];
const OPERATORS_DATE = ["is", "!=", ">", "<", ">=", "<=", "isnull"];
const OPERATORS_BOOL = ["is", "isnull"];
const OPERATORS_CHOICE = ["is", "!=", "in", "not_in", "isnull"]; // reference fields & enums

const getOperatorsForField = (fType, isChoice) => {
  if (isChoice) return OPERATORS_CHOICE;
  const t = fType || "";
  if (t.includes("bool")) return OPERATORS_BOOL;
  if (t.includes("timestamp") || t.includes("date")) return OPERATORS_DATE;
  if (t.includes("int") || t.includes("numeric") || t.includes("decimal") || t.includes("float")) {
    return OPERATORS_NUMBER;
  }
  return OPERATORS_TEXT;
};

const getOperatorLabel = (op, fType) => {
  const t = fType || "";
  if ((t.includes("timestamp") || t.includes("date")) && DATE_OPERATOR_LABELS[op]) {
    return DATE_OPERATOR_LABELS[op];
  }
  return OPERATOR_LABELS[op] || op;
};

const fieldTypeIcon = (f) => {
  if (f?.reference_table) return Link2;
  const t = (f?.field_type || "").toUpperCase();
  if (t === "ENUM" || t === "CHOICE") return List;
  if (t.includes("BOOL")) return ToggleLeft;
  if (t.includes("TIMESTAMP") || t.includes("DATE")) return CalendarDays;
  if (t.includes("INT") || t.includes("NUMERIC") || t.includes("DECIMAL")) return Hash;
  return TypeIcon;
};

const FilterBuilderComponent = ({
  filterTable,
  filterValue,
  onChange,
  token,
  apiBaseUrl,
  defaultLogic = "AND",
  isEmbeddedInListView = false,
}) => {
  const [filterGroup, setFilterGroup] = useState({ logic: defaultLogic, conditions: [] });
  const [recordCount, setRecordCount] = useState(null);
  const [referenceOptions, setReferenceOptions] = useState({});
  const [dotwalkChains, setDotwalkChains] = useState({});
  const [fieldTypes, setFieldTypes] = useState({});
  const [enumOptions, setEnumOptions] = useState({});
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const inputStyle = {
    padding: "8px 10px",
    borderRadius: "8px",
    border: "1px solid var(--line, #dfe7e5)",
    fontSize: "13px",
    minWidth: 160,
    backgroundColor: "#fff",
    color: "var(--ink, #0f1a1c)",
    outline: "none",
  };

  // propagate changes automatically in non-embedded mode
  useEffect(() => {
    if (!isEmbeddedInListView) {
      onChange(JSON.stringify({ filterGroup }));
      setHasPendingChanges(false);
    }
  }, [filterGroup]); // eslint-disable-line

  // init from prop + prewarm dotwalks
  // init from prop + prewarm dotwalks
useEffect(() => {
  if (!filterTable) return;

  (async () => {
    try {
      const parsed = JSON.parse(filterValue || "{}");
      const initial = parsed.filterGroup || { logic: defaultLogic, conditions: [] };

      // Always start with 1 condition (AND chain); SN-style UX
      const startGroup =
        Array.isArray(initial.conditions) && initial.conditions.length > 0
          ? initial
          : { logic: defaultLogic, conditions: [{ field: "", operator: "is", value: "", next_logic: "AND" }] };

      setFilterGroup(startGroup);

      // prepare dotwalk chains & metadata
      await initDotwalksRecursively(startGroup);
      await prefetchReferenceAndEnums(startGroup);

      // ensure root chain exists for first row
      if (!dotwalkChains["0"]) await initDotwalkChain("0");
    } catch {
      const fallback = { logic: defaultLogic, conditions: [{ field: "", operator: "is", value: "", next_logic: "AND" }] };
      setFilterGroup(fallback);
      await initDotwalkChain("0");
    }
  })();
}, [filterTable]); // eslint-disable-line


  // ---------- helpers ----------

  // cache table->fields to avoid refetch spam

  // Pretty breadcrumb text for a dot-walk path like "caller.department.manager"
// Rebuild the dotwalk chain from the currently committed path (e.g. "caller.department.manager")
const resetChainToCommitted = async (pathKey, filterTable, currentPath, ensureFieldList, setDotwalkChains) => {
  const parts = String(currentPath || "").split(".").filter(Boolean);
  let table = filterTable;
  const newChain = [];

  // level 0
  let fields = await ensureFieldList(table);
  newChain.push({ parent: table, fields, selected: parts[0] || "" });

  // deeper levels if reference fields exist
  for (let i = 0; i < parts.length; i++) {
    const fName = parts[i];
    const meta = (fields || []).find((f) => f.name === fName);
    if (!meta) break;
    if (!meta.reference_table) break;

    table = meta.reference_table;
    fields = await ensureFieldList(table);
    newChain.push({
      parent: table,
      fields,
      selected: parts[i + 1] || "",
    });
  }

  setDotwalkChains((prev) => ({ ...prev, [pathKey]: newChain }));
};

const fieldCacheRef = useRef(new Map());
const ensureFieldList = async (table) => {
  const cache = fieldCacheRef.current;
  if (cache.has(table)) return cache.get(table);
  const res = await axios.get(
    `${apiBaseUrl}/search/table_fields/?table_id.name=${table}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const list = res.data.records || [];
  cache.set(table, list);
  return list;
};

// Pretty “Field ▸ Subfield ▸ …” label
const formatPathLabel = (path) => (path || "").split(".").filter(Boolean).join(" ▸ ") || "Select field…";

  const prefetchReferenceAndEnums = async (group) => {
    const walk = async (g) => {
      for (const cond of g.conditions || []) {
        if (cond.conditions) {
          await walk(cond);
          continue;
        }
        if (!cond.field) continue;

        // resolve path chain for types & refs
        let currentTable = filterTable;
        const parts = cond.field.split(".");
        let lastFieldMeta = null;

        for (let i = 0; i < parts.length; i++) {
          const meta = await axios.get(
            `${apiBaseUrl}/search/table_fields/?table_id.name=${currentTable}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const f = (meta.data.records || []).find((x) => x.name === parts[i]);
          if (!f) break;
          lastFieldMeta = f;

          // store type
          const fullPath = parts.slice(0, i + 1).join(".");
          if (f.field_type) {
            setFieldTypes((prev) => ({ ...prev, [fullPath]: f.field_type }));
          }

          // enum
          if (Array.isArray(f.allowed_values) && f.allowed_values.length) {
            setEnumOptions((prev) => ({ ...prev, [fullPath]: f.allowed_values }));
          }

          // reference => step down
          if (f.reference_table) currentTable = f.reference_table;
        }

        // prefetch reference options for any operator that needs a value picker
        if (lastFieldMeta?.reference_table && ["is", "in", "not_in"].includes(cond.operator)) {
          await fetchReferenceValues(lastFieldMeta.reference_table, cond.field);
        }
      }
    };
    await walk(group);
  };

  const initDotwalksRecursively = async (group, parentPath = []) => {
    for (let i = 0; i < (group.conditions || []).length; i++) {
      const cond = group.conditions[i];
      const pathKey = [...parentPath, i].join("-");
      if (cond.conditions) {
        await initDotwalksRecursively(cond, [...parentPath, i]);
      } else if (cond.field) {
        let currentTable = filterTable;
        const path = cond.field.split(".");
        const chain = [];

        for (let j = 0; j < path.length; j++) {
          const res = await axios.get(
            `${apiBaseUrl}/search/table_fields/?table_id.name=${currentTable}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const fields = res.data.records || [];
          const selected = path[j];
          chain.push({ parent: currentTable, fields, selected });

          const selectedField = fields.find((f) => f.name === selected);
          if (selectedField?.field_type) {
            const fullPath = path.slice(0, j + 1).join(".");
            setFieldTypes((prev) => ({ ...prev, [fullPath]: selectedField.field_type }));
            if (Array.isArray(selectedField.allowed_values) && selectedField.allowed_values.length) {
              setEnumOptions((prev) => ({ ...prev, [fullPath]: selectedField.allowed_values }));
            }
          }

          if (selectedField?.reference_table) {
            currentTable = selectedField.reference_table;
          } else {
            break;
          }
        }
        setDotwalkChains((prev) => ({ ...prev, [pathKey]: chain }));
      } else {
        await initDotwalkChain(pathKey);
      }
    }
  };

  const fetchReferenceValues = async (referenceTable, dotwalkPath) => {
    try {
      const res = await axios.get(`${apiBaseUrl}/${referenceTable}/?limit=100`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const fieldMeta = await axios.get(
        `${apiBaseUrl}/search/table_fields/?table_id.name=${referenceTable}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const displayField = fieldMeta.data.records?.find((f) => f.display)?.name || "id";
      // backend list endpoint may return array or {records}
      const list = Array.isArray(res.data)
        ? res.data
        : res.data.records || res.data || [];

      const options = list.map((r) => ({
        id: r.id,
        label: r[displayField] || r.id,
      }));

      setReferenceOptions((prev) => ({ ...prev, [dotwalkPath]: options }));
    } catch (err) {
      console.error("Failed to fetch reference values", err);
    }
  };

  const handleFieldSelect = async (pathKey, level, selectedField) => {
    const chain = dotwalkChains[pathKey] || [];
    const fieldInfo = chain[level]?.fields?.find((f) => f.name === selectedField);

    const newChain = [...chain.slice(0, level + 1)];
    newChain[level] = {
      parent: chain[level]?.parent || filterTable,
      fields: chain[level]?.fields || [],
      selected: selectedField,
    };

    const fullPath = newChain.map((c) => c.selected).filter(Boolean).join(".");
    if (fieldInfo?.field_type && fullPath) {
      setFieldTypes((prev) => ({ ...prev, [fullPath]: fieldInfo.field_type }));
      if (Array.isArray(fieldInfo.allowed_values) && fieldInfo.allowed_values.length) {
        setEnumOptions((prev) => ({ ...prev, [fullPath]: fieldInfo.allowed_values }));
      }
    }

    setDotwalkChains((prev) => ({ ...prev, [pathKey]: [...newChain] }));

    if (fieldInfo?.reference_table) {
      const res = await axios.get(
        `${apiBaseUrl}/search/table_fields/?table_id.name=${fieldInfo.reference_table}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      newChain[level + 1] = {
        parent: fieldInfo.reference_table,
        fields: res.data.records || [],
        selected: "",
      };
      setDotwalkChains((prev) => ({ ...prev, [pathKey]: [...newChain] }));
    }

    updateCondition(pathKey.split("-").map((n) => parseInt(n, 10)), "field", fullPath);

    // if operator is "is" and this is reference → fetch options
    const idxPath = pathKey.split("-").map((n) => parseInt(n, 10));
    let pointer = filterGroup;
    for (let i = 0; i < idxPath.length - 1; i++) pointer = pointer.conditions[idxPath[i]];
    const fieldCond = pointer.conditions[idxPath[idxPath.length - 1]];
    if (fieldInfo?.reference_table && fieldCond?.operator === "is") {
      await fetchReferenceValues(fieldInfo.reference_table, fullPath);
    }
  };

  const getFullDotwalkPath = (idx) => {
    const chain = dotwalkChains[idx] || [];
    return chain.map((d) => d.selected).filter(Boolean).join(".");
  };

  const updateGroupLogic = (path, newLogic) => {
    const newGroup = JSON.parse(JSON.stringify(filterGroup));
    let pointer = newGroup;
    for (let i = 0; i < path.length; i++) pointer = pointer.conditions[path[i]];
    pointer.logic = newLogic;
    setFilterGroup(newGroup);
    setHasPendingChanges(true);
  };

  const isInOrChain = (group, path) => {
    const idx = path[path.length - 1];
    const parent = getGroupAtPath(group, path);
    if (!parent || !parent.conditions || idx === undefined) return false;

    const current = parent.conditions[idx];
    const prev = parent.conditions[idx - 1];
    const next = parent.conditions[idx + 1];

    return (
      (prev && prev.next_logic === "OR") ||
      (current && current.next_logic === "OR") ||
      (next && current.next_logic === "OR")
    );
  };

  // ---------- renderers ----------
  const renderConditionGroup = (group = filterGroup, path = []) => {
    const isRoot = path.length === 0;

    return (
      <div
        style={{
          border: "1px solid var(--line, #dfe7e5)",
          padding: 12,
          marginBottom: 12,
          borderRadius: 12,
          background: "#ffffff",
          boxShadow: "var(--shadow-xs, 0 1px 2px rgba(9,20,19,.05))",
        }}
      >
        {/* Group header */}
        {!isRoot && (
          <div
            style={{
              marginBottom: 10,
              display: "flex",
              alignItems: "center",
              gap: 8,
              justifyContent: "space-between",
              background: "var(--card-2, #f1f5f4)",
              border: "1px solid var(--line, #dfe7e5)",
              padding: "8px 10px",
              borderRadius: 10,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Boxes size={14} color="var(--brand, #0d7d6f)" />
              <strong>Group</strong>
              <LogicToggle value={group.logic || "AND"} onChange={(v) => updateGroupLogic(path, v)} />
            </div>

            <button
              onClick={() => removeGroup(path)}
              style={{ ...btnDanger, display: "inline-flex", alignItems: "center", gap: 6 }}
              type="button"
              title="Remove group"
            >
              <X size={13} /> Remove
            </button>
          </div>
        )}

        {/* Conditions */}
        {group.conditions.map((cond, idx) => {
          const newPath = [...path, idx];
          if (cond.conditions) return renderConditionGroup(cond, newPath);
          return renderConditionRow(cond, newPath);
        })}

        {/* Group footer actions */}
        <div style={{ marginTop: 8, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => addCondition(path)} style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 6 }} type="button">
            <Plus size={13} /> Add Condition
          </button>
          <button onClick={() => addGroup(path)} style={{ ...btnGhost, display: "inline-flex", alignItems: "center", gap: 6 }} type="button">
            <Boxes size={13} /> Add Group
          </button>
        </div>
      </div>
    );
  };

  const renderPathChips = (dotwalk) => {
    if (!dotwalk?.length) return null;
    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {dotwalk
          .filter((d) => d.selected)
          .map((d, i) => (
            <span
              key={`${d.parent}-${d.selected}-${i}`}
              style={{
                padding: "4px 8px",
                background: "var(--brand-wash, #e6f4f1)",
                color: "var(--brand-dark, #085f54)",
                border: "1px solid transparent",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {d.selected}
            </span>
          ))}
      </div>
    );
  };

const renderConditionRow = (cond, path) => {
  const pathKey = path.join("-");
  const dotwalk = dotwalkChains[pathKey] || [];
  const fType = (fieldTypes[cond.field] || "").toLowerCase();
  const lastLevel = dotwalk[dotwalk.length - 1];
  const selectedFieldMeta = (lastLevel?.fields || []).find((f) => f.name === lastLevel?.selected);
  const isChoice = !!selectedFieldMeta?.reference_table || (Array.isArray(enumOptions[cond.field]) && enumOptions[cond.field].length > 0);
  const availableOperators = getOperatorsForField(fType, isChoice);

  return (
    <div
      key={pathKey}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: 10,
        marginBottom: 10,
        background: "var(--card-2, #f1f5f4)",
        borderRadius: 12,
        border: "1px solid var(--line, #dfe7e5)",
      }}
    >
      {/* FIELD (dot-walk) */}
    <FieldDotwalkPicker
  pathKey={pathKey}
  chain={dotwalk}
  currentPath={cond.field || ""}
  onCommit={(level, name) => handleFieldSelect(pathKey, level, name)}
  placeholder="Select field…"
 onRequestClose={async () => {
   await resetChainToCommitted(
     pathKey,
     filterTable,
     cond.field || "",
     ensureFieldList,
     setDotwalkChains
   );
 }}
/>

      {/* OPERATOR */}
      <select
        style={{ ...inputStyle, minWidth: 150 }}
        value={(cond.operator || "").trim()}
        onChange={(e) => {
          const nextOp = e.target.value.trim();
          updateCondition(path, nextOp === "isnull" ? { operator: nextOp, value: "true" } : { operator: nextOp });
        }}
        title="Operator"
      >
        <option value="">— Operator —</option>
        {availableOperators.map((op) => (
          <option key={op} value={op}>{getOperatorLabel(op, fType)}</option>
        ))}
      </select>

      {/* VALUE */}
      {renderValueInput(cond, path, fType)}

      {/* NEXT LOGIC (tiny pill) */}
      <LogicToggle
        value={cond.next_logic || "AND"}
        onChange={(v) => updateCondition(path, "next_logic", v)}
        compact
      />

      {/* ROW ACTIONS */}
      <button onClick={() => addCondition(path.slice(0, -1))} style={{ ...btnGhost, padding: "8px 10px" }} type="button" title="Add new after">
        <Plus size={14} />
      </button>
      <button onClick={() => removeCondition(path)} style={{ ...btnDanger, padding: "8px 10px" }} type="button" title="Remove">
        <X size={14} />
      </button>
    </div>
  );
};


  const renderValueInput = (cond, path, fType) => {
    // "is empty" / "is not empty" — no free-text value, just a yes/no choice
    // mapping straight onto the backend's isnull contract (value: "true"/"false").
    if (cond.operator === "isnull") {
      return (
        <select
          style={{ ...inputStyle, minWidth: 160 }}
          value={(cond.value ?? "true").toString()}
          onChange={(e) => updateCondition(path, "value", e.target.value)}
        >
          <option value="true">Is empty</option>
          <option value="false">Is not empty</option>
        </select>
      );
    }

    const isSetOp = cond.operator === "in" || cond.operator === "not_in";

    // reference field: single-select for "is", multi-select for "in"/"not_in"
    if (referenceOptions[cond.field] && (cond.operator === "is" || isSetOp)) {
      if (isSetOp) {
        const selectedIds = String(cond.value || "").split(",").map((v) => v.trim()).filter(Boolean);
        return (
          <select
            multiple
            style={{ ...inputStyle, minWidth: 220, height: 84 }}
            value={selectedIds}
            onChange={(e) =>
              updateCondition(path, "value", Array.from(e.target.selectedOptions).map((o) => o.value).join(","))
            }
          >
            {referenceOptions[cond.field].map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }
      return (
        <select
          style={{ ...inputStyle, minWidth: 220 }}
          value={cond.value}
          onChange={(e) => updateCondition(path, "value", e.target.value)}
        >
          <option value="">— Select —</option>
          {referenceOptions[cond.field].map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }

    // enum: single-select for "is", multi-select for "in"/"not_in"
    if (Array.isArray(enumOptions[cond.field]) && enumOptions[cond.field].length) {
      if (isSetOp) {
        const selectedVals = String(cond.value || "").split(",").map((v) => v.trim()).filter(Boolean);
        return (
          <select
            multiple
            style={{ ...inputStyle, minWidth: 180, height: 84 }}
            value={selectedVals}
            onChange={(e) =>
              updateCondition(path, "value", Array.from(e.target.selectedOptions).map((o) => o.value).join(","))
            }
          >
            {enumOptions[cond.field].map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        );
      }
      return (
        <select
          style={{ ...inputStyle, minWidth: 180 }}
          value={cond.value}
          onChange={(e) => updateCondition(path, "value", e.target.value)}
        >
          <option value="">— Select —</option>
          {enumOptions[cond.field].map((v) => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </select>
      );
    }

    // boolean → toggle
    if (fType.includes("bool")) {
      return (
        <select
          style={{ ...inputStyle, minWidth: 120 }}
          value={(cond.value ?? "").toString()}
          onChange={(e) => updateCondition(path, "value", e.target.value)}
        >
          <option value="">—</option>
          <option value="true">true</option>
          <option value="false">false</option>
        </select>
      );
    }

    // datetime & date
    if (fType.includes("timestamp")) {
      return (
        <input
          style={{ ...inputStyle, minWidth: 220 }}
          type="datetime-local"
          value={cond.value || ""}
          onChange={(e) => updateCondition(path, "value", e.target.value)}
        />
      );
    }
    if (fType.includes("date")) {
      return (
        <input
          style={{ ...inputStyle, minWidth: 180 }}
          type="date"
          value={cond.value || ""}
          onChange={(e) => updateCondition(path, "value", e.target.value)}
        />
      );
    }

    return (
      <input
        style={{ ...inputStyle, minWidth: isSetOp ? 280 : 220 }}
        placeholder={isSetOp ? "Comma-separated values (A,B,C)" : ""}
        value={cond.value || ""}
        onChange={(e) => updateCondition(path, "value", e.target.value)}
      />
    );
  };

  // ---------- state updates ----------
  // Accepts either (path, key, value) for a single field, or (path, { key: value, ... })
  // to apply several fields atomically in one state update (avoids two sequential
  // setFilterGroup calls racing against the same stale `filterGroup` closure).
  const updateCondition = async (path, keyOrPatch, value) => {
    const patch = typeof keyOrPatch === "string" ? { [keyOrPatch]: value } : keyOrPatch;
    if (typeof patch.operator === "string") patch.operator = patch.operator.trim();

    const newGroup = JSON.parse(JSON.stringify(filterGroup));
    let pointer = newGroup;
    for (let i = 0; i < path.length - 1; i++) pointer = pointer.conditions[path[i]];

    const cond = pointer.conditions[path[path.length - 1]];
    Object.assign(cond, patch);

    setFilterGroup(newGroup);
    setHasPendingChanges(true);

    // Fetch reference/enum values whenever the operator settles on one that
    // needs a value picker (is / in / not_in), not just "is".
    const needsOptions = ["is", "in", "not_in"].includes(patch.operator);
    if (needsOptions && cond.field) {
      const pathParts = cond.field.split(".");
      let currentTable = filterTable;
      let currentField = null;

      for (let i = 0; i < pathParts.length; i++) {
        const res = await axios.get(
          `${apiBaseUrl}/search/table_fields/?table_id.name=${currentTable}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const fieldInfo = res.data.records.find((f) => f.name === pathParts[i]);
        if (!fieldInfo) break;

        currentField = fieldInfo;
        if (fieldInfo.reference_table) currentTable = fieldInfo.reference_table;
      }

      if (currentField?.reference_table) {
        await fetchReferenceValues(currentField.reference_table, cond.field);
      }
    }
  };

  const getGroupAtPath = (group, path) => {
    let current = group;
    for (const i of path.slice(0, -1)) {
      if (!current.conditions[i]) return null;
      current = current.conditions[i];
    }
    return current;
  };

  const addCondition = (path = []) => {
    const newGroup = JSON.parse(JSON.stringify(filterGroup));
    let pointer = newGroup;
    for (let i = 0; i < path.length; i++) pointer = pointer.conditions[path[i]];
    pointer.conditions.push({ field: "", operator: "is", value: "" });

    const newPathKey = [...path, pointer.conditions.length - 1].join("-");

    // Show the new row immediately — don't gate it behind the field-list
    // fetch. initDotwalkChain fills in the picker's options once it lands.
    setFilterGroup(newGroup);
    setHasPendingChanges(true);
    initDotwalkChain(newPathKey);
  };

  const removeCondition = (path) => {
    const newGroup = JSON.parse(JSON.stringify(filterGroup));
    let pointer = newGroup;
    for (let i = 0; i < path.length - 1; i++) pointer = pointer.conditions[path[i]];
    pointer.conditions.splice(path[path.length - 1], 1);
    setFilterGroup(newGroup);
    setHasPendingChanges(true);
  };

  const addGroup = (path = []) => {
    const newGroup = JSON.parse(JSON.stringify(filterGroup));
    let pointer = newGroup;
    for (let i = 0; i < path.length; i++) pointer = pointer.conditions[path[i]];
    pointer.conditions.push({ logic: "AND", conditions: [] });
    setFilterGroup(newGroup);
    setHasPendingChanges(true);
  };

  const removeGroup = (path) => {
    const newGroup = JSON.parse(JSON.stringify(filterGroup));
    let pointer = newGroup;
    for (let i = 0; i < path.length - 1; i++) pointer = pointer.conditions[path[i]];
    pointer.conditions.splice(path[path.length - 1], 1);
    setFilterGroup(newGroup);
    setHasPendingChanges(true);
  };


  const fetchCount = async () => {
    try {
      const params = new URLSearchParams();
      params.set("filter_json", JSON.stringify(filterGroup));

      const response = await axios.get(`${apiBaseUrl}/search/${filterTable}/`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      setRecordCount(response.data.total);
    } catch (error) {
      console.error("Failed to fetch count", error);
    }
  };

  const initDotwalkChain = async (idx) => {
    try {
      const res = await axios.get(
        `${apiBaseUrl}/search/table_fields/?table_id.name=${filterTable}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const chain = [
        { parent: filterTable, fields: res.data.records || [], selected: "" },
      ];

      setDotwalkChains((prev) => ({ ...prev, [idx]: chain }));
    } catch (e) {
      console.error("Failed to init dotwalkChain", e);
    }
  };

  // ---------- UI ----------
  return (
    <div
      style={{
        padding: 16,
        background: "#ffffff",
        border: "1px solid var(--line, #dfe7e5)",
        borderRadius: 12,
        boxShadow: "var(--shadow-sm, 0 1px 3px rgba(9,20,19,.07))",
        fontSize: 13,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Filter size={15} color="var(--brand, #0d7d6f)" />
          <strong>Advanced Filter</strong>
          {isEmbeddedInListView && (
            <span
              style={{
                marginLeft: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "3px 9px",
                borderRadius: 999,
                background: hasPendingChanges ? "var(--warn-wash, #fef3e2)" : "var(--good-wash, #e9f8ee)",
                color: hasPendingChanges ? "var(--warn, #d97706)" : "var(--good, #16a34a)",
                border: "1px solid transparent",
                fontWeight: 700,
                fontSize: 11,
              }}
            >
              {hasPendingChanges ? "Unsaved changes" : (
                <>
                  <CircleCheck size={11} /> Synced
                </>
              )}
            </span>
          )}
        </div>

        {!isEmbeddedInListView && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={fetchCount} style={{ ...btnPrimary, display: "inline-flex", alignItems: "center", gap: 6 }} type="button">
              <RefreshCw size={13} /> Preview Count
            </button>
            {recordCount !== null && (
              <div
                style={{
                  alignSelf: "center",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  color: "var(--ink, #0f1a1c)",
                  background: "var(--card-2, #f1f5f4)",
                  border: "1px solid var(--line, #dfe7e5)",
                  padding: "6px 10px",
                  borderRadius: 8,
                }}
              >
                <CircleCheck size={13} color="var(--good, #16a34a)" /> Matches: <strong>{recordCount}</strong>
              </div>
            )}
          </div>
        )}
      </div>

      {renderConditionGroup(filterGroup)}

      {/* Footer for embedded mode */}
      {isEmbeddedInListView && (
        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 10,
            borderTop: "1px dashed var(--line, #dfe7e5)",
            paddingTop: 10,
          }}
        >
          <button
            disabled={isRunning || !hasPendingChanges}
            onClick={async () => {
              setIsRunning(true);
              await new Promise((r) => setTimeout(r, 150)); // tiny UX delay
              onChange(JSON.stringify({ filterGroup }));
              setIsRunning(false);
              setHasPendingChanges(false);
            }}
            style={{
              ...btnPrimary,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              opacity: isRunning || !hasPendingChanges ? 0.7 : 1,
              cursor: isRunning || !hasPendingChanges ? "not-allowed" : "pointer",
            }}
            type="button"
            title="Apply to list"
          >
            {isRunning ? (
              <Loader2 size={13} style={{ animation: "lv-spin 0.8s linear infinite" }} />
            ) : (
              <Rocket size={13} />
            )}
            {isRunning ? "Running..." : "Run Filter"}
          </button>
        </div>
      )}
    </div>
  );
};

/* ---------- small style tokens ---------- */
const btnBase = {
  border: "1px solid var(--line, #dfe7e5)",
  background: "#fff",
  color: "var(--ink-2, #1e2e30)",
  padding: "8px 12px",
  borderRadius: 8,
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 13,
  transition: "all .15s ease",
};

const btnPrimary = {
  ...btnBase,
  background: "var(--brand, #0d7d6f)",
  borderColor: "var(--brand, #0d7d6f)",
  color: "#fff",
};

const btnGhost = {
  ...btnBase,
  background: "#fff",
};

const btnDanger = {
  ...btnBase,
  background: "var(--danger, #dc2626)",
  borderColor: "var(--danger, #dc2626)",
  color: "#fff",
};

/** Segmented AND/OR pill — replaces plain <select> logic dropdowns with a
 *  proper two-state toggle, matching how condition/group logic is displayed
 *  everywhere else in the tree (a pill, not a form control). */
const LogicToggle = ({ value, onChange, compact = false }) => {
  const optionStyle = (option) => {
    const active = value === option;
    return {
      padding: compact ? "5px 10px" : "6px 12px",
      fontSize: compact ? 11 : 12,
      fontWeight: 800,
      letterSpacing: "0.03em",
      border: "none",
      cursor: "pointer",
      background: active ? "var(--brand, #0d7d6f)" : "transparent",
      color: active ? "#fff" : "var(--muted, #5c6d6f)",
      transition: "background .12s ease, color .12s ease",
    };
  };

  return (
    <div
      role="group"
      aria-label="Logic"
      style={{
        display: "inline-flex",
        borderRadius: 999,
        background: "var(--card-2, #f1f5f4)",
        border: "1px solid var(--line, #dfe7e5)",
        padding: 2,
      }}
    >
      <button type="button" style={{ ...optionStyle("AND"), borderRadius: 999 }} onClick={() => onChange("AND")}>
        AND
      </button>
      <button type="button" style={{ ...optionStyle("OR"), borderRadius: 999 }} onClick={() => onChange("OR")}>
        OR
      </button>
    </div>
  );
};

/** Click-driven dot-walk picker that renders in a portal (above all), does not
 *  get clipped by ancestor overflow/stacking contexts, and resets to the
 *  committed path on close. Clicking a reference field commits it and reveals
 *  the next column; clicking a leaf field commits and can be closed via
 *  Escape or an outside click.
 */
const FieldDotwalkPicker = ({
  pathKey,
  chain = [],
  currentPath = "",
  onCommit,
  onRequestClose,
  placeholder = "Select field…",
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const anchorRef = useRef(null);
  const portalRef = useRef(null);
  const searchRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, maxWidth: 720 });

  const safeChain = chain.length ? chain : [{ parent: "", fields: [], selected: "" }];

  // Human-readable "Department ▸ Manager" instead of the raw dotwalk path
  // ("department.manager_id") that the backend actually stores/consumes.
  const displayLabel = safeChain
    .filter((lvl) => lvl.selected)
    .map((lvl) => (lvl.fields || []).find((f) => f.name === lvl.selected)?.label || lvl.selected)
    .join(" ▸ ");

  const openMenu = () => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const estimatedWidth = 720; // 3–4 columns; container scrolls if more
    const left = Math.min(
      Math.max(8, rect.left),
      Math.max(8, window.innerWidth - estimatedWidth - 8)
    );
    const top = Math.min(
      rect.bottom + 6,
      Math.max(8, window.innerHeight - 320 - 8)
    );
    setPos({ top, left, maxWidth: estimatedWidth });
    setQuery("");
    setOpen(true);
    setTimeout(() => searchRef.current?.focus(), 0);
  };

  const closeMenu = () => {
    setOpen(false);
    setQuery("");
    // tell parent to rebuild chain from committed value (forget any in-progress drill)
    onRequestClose?.();
  };

  // Close when clicking outside or pressing ESC
  useEffect(() => {
    if (!open) return;

    const onDocDown = (e) => {
      const inAnchor = anchorRef.current?.contains(e.target);
      const inPortal = portalRef.current?.contains(e.target);
      if (!inAnchor && !inPortal) closeMenu();
    };
    const onKey = (e) => {
      if (e.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleClick = (level, f) => {
    // Commit the clicked field segment (leaf or not)
    onCommit(level, f.name);
    // Keep open for further dotwalking; user can press ESC/click outside to close.
  };

  const matchesQuery = (f) => {
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (f.label || f.name || "").toLowerCase().includes(q) || (f.name || "").toLowerCase().includes(q);
  };

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={() => (open ? closeMenu() : openMenu())}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          minWidth: 200,
          justifyContent: "space-between",
          position: "relative",
          zIndex: 1,
          padding: "8px 12px",
          borderRadius: 999,
          border: `1px solid ${currentPath ? "var(--brand, #0d7d6f)" : "var(--line, #dfe7e5)"}`,
          background: currentPath ? "var(--brand-wash, #e6f4f1)" : "#fff",
          color: currentPath ? "var(--brand-dark, #085f54)" : "var(--ink-2, #1e2e30)",
          fontWeight: 700,
          fontSize: 13,
          cursor: "pointer",
          transition: "all .12s ease",
        }}
        title={displayLabel || placeholder}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {displayLabel || placeholder}
        </span>
        <ChevronDown size={13} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s ease" }} />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={portalRef}
            className="fbc-popover-in"
            style={{
              position: "fixed",
              zIndex: 100000,               // above ListView
              top: pos.top,
              left: pos.left,
              display: "flex",
              flexDirection: "column",
              background: "#fff",
              border: "1px solid var(--line, #dfe7e5)",
              borderRadius: 14,
              boxShadow: "var(--shadow-xl, 0 24px 60px rgba(8,95,84,.18))",
              maxWidth: pos.maxWidth,
              overflow: "hidden",
            }}
          >
            <div style={{ position: "relative", padding: "8px 8px 6px", borderBottom: "1px solid var(--line, #dfe7e5)" }}>
              <Search size={13} style={{ position: "absolute", left: 18, top: "50%", transform: "translateY(-50%)", color: "var(--muted, #5c6d6f)" }} />
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search fields…"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: "7px 10px 7px 30px",
                  borderRadius: 8,
                  border: "1px solid var(--line, #dfe7e5)",
                  fontSize: 12.5,
                  outline: "none",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: 6, padding: 8, maxHeight: 300, overflowX: "auto", overflowY: "hidden" }}>
              {safeChain.map((lvl, levelIdx) => {
                const visibleFields = (lvl.fields || []).filter(matchesQuery);
                return (
                  <div
                    key={`${pathKey}-col-${levelIdx}`}
                    style={{
                      minWidth: 230,
                      maxHeight: 280,
                      overflowY: "auto",
                      borderRight:
                        levelIdx < safeChain.length - 1 ? "1px solid var(--line, #dfe7e5)" : "none",
                      paddingRight: 6,
                    }}
                  >
                    <div style={{ fontSize: 11, color: "var(--muted, #5c6d6f)", margin: "4px 6px 6px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {lvl.parent || "Fields"}
                    </div>

                    {visibleFields.length === 0 && (
                      <div style={{ padding: "10px 10px", fontSize: 12.5, color: "var(--muted, #5c6d6f)" }}>No matches</div>
                    )}

                    {visibleFields.map((f) => {
                      const isSelected = lvl.selected === f.name;
                      const isRef = !!f.reference_table;
                      const FieldIcon = fieldTypeIcon(f);
                      return (
                        <div
                          key={f.name}
                          onClick={() => handleClick(levelIdx, f)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            padding: "8px 10px",
                            borderRadius: 8,
                            cursor: "pointer",
                            background: isSelected ? "var(--brand-wash, #e6f4f1)" : "transparent",
                            color: isSelected ? "var(--brand-dark, #085f54)" : "var(--ink-2, #1e2e30)",
                            fontWeight: isSelected ? 700 : 500,
                            whiteSpace: "nowrap",
                            transition: "background .1s ease",
                          }}
                          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = "var(--card-2, #f1f5f4)"; }}
                          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = "transparent"; }}
                          title={f.label || f.name}
                        >
                          <span style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                            <FieldIcon size={13} color={isSelected ? "var(--brand-dark, #085f54)" : "var(--muted, #5c6d6f)"} style={{ flexShrink: 0 }} />
                            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{f.label || f.name}</span>
                          </span>
                          {isRef && (
                            <ChevronRight size={13} style={{ opacity: 0.5, flexShrink: 0 }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};



export default FilterBuilderComponent;
